import { useEffect, useState } from 'react'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Dona de segmentos.
 *
 * Sustituye a las barras apiladas en toda la app. Una barra obliga a comparar
 * longitudes contra un origen; una dona muestra la proporción como parte de un
 * todo cerrado, que es justo la pregunta cuando se reparte un saldo. Además
 * libera el centro, y ahí va la cifra que importa — sin gastar una línea más de
 * pantalla.
 *
 * Se dibuja a mano con SVG en vez de usar una librería de gráficas: son arcos
 * de circunferencia, y traer 40 kB de dependencia para eso encarecería el
 * arranque de una app que se abre veinte veces al día.
 *
 * Al tocar un segmento, el centro muestra ese tramo. Es el equivalente táctil
 * del hover: en un teléfono no existe puntero al que reaccionar.
 */

export interface DonutSegment {
  key: string
  label: string
  value: number
  /** Variable CSS del color, p. ej. `var(--commit-free)`. */
  color: string
}

const SIZE = 120
const CENTER = SIZE / 2
const STROKE = 15
const RADIUS = CENTER - STROKE / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/**
 * Cuerpo del monto del centro, en píxeles.
 *
 * El hueco del anillo mide 90 px y a la altura del texto deja unos 74 útiles.
 * A 19 px fijos, «$11,400» cabía pero «$10,074.50» no: se montaba sobre el arco
 * por los dos lados, y con centavos pasa en cuanto el monto llega a cinco
 * cifras. Se estima el ancho con las proporciones de la tipografía —cifras
 * tabulares de 0.6 em, signo de 0.56, separadores de 0.26— y se reduce lo justo
 * para que quepa, con 19 px de tope para que los montos cortos no cambien.
 */
function cuerpoDelCentro(texto: string): number {
  let em = 0
  for (const c of texto) em += c === ',' || c === '.' ? 0.26 : c === '$' ? 0.56 : 0.6
  return Math.max(11, Math.min(19, Math.floor(74 / em)))
}

/** Separación entre arcos, en unidades de longitud de trazo. */
const GAP = 3
/** Arco mínimo visible: sin este piso, un tramo real pero pequeño desaparece. */
const MIN_ARC = 6

export function Donut({
  segments,
  centerLabel,
  centerValue,
  onSelect,
  className,
}: {
  segments: readonly DonutSegment[]
  centerLabel: string
  centerValue: string
  /** Se llama al tocar un segmento; `null` al deseleccionar. */
  onSelect?: (key: string | null) => void
  className?: string
}) {
  const [drawn, setDrawn] = useState(false)
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    // Un fotograma de margen para que el navegador registre el estado inicial y
    // la transición tenga desde dónde animar.
    const id = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const visibles = segments.filter((s) => s.value > 0)
  const total = visibles.reduce((sum, s) => sum + s.value, 0)

  const arcos = repartirArcos(visibles, total)

  const seleccionar = (key: string): void => {
    haptic('selection')
    const siguiente = active === key ? null : key
    setActive(siguiente)
    onSelect?.(siguiente)
  }

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: SIZE, height: SIZE }}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="size-full -rotate-90"
        role="img"
        aria-label={
          total === 0
            ? 'Sin datos todavía'
            : visibles.map((s) => `${s.label}: ${Math.round((s.value / total) * 100)}%`).join('. ')
        }
      >
        {/* Pista de fondo: mantiene la forma del anillo cuando aún no hay datos
            y evita que la dona «aparezca de la nada» al cargar. */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill="none"
          stroke="var(--surface-sunken)"
          strokeWidth={STROKE}
        />

        {arcos.map(({ segment, length, offset }) => {
          const atenuado = active !== null && active !== segment.key
          return (
            <circle
              key={segment.key}
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke={segment.color}
              strokeWidth={active === segment.key ? STROKE + 3 : STROKE}
              strokeLinecap="round"
              strokeDasharray={
                drawn ? `${length} ${CIRCUMFERENCE - length}` : `0 ${CIRCUMFERENCE}`
              }
              strokeDashoffset={-offset}
              opacity={atenuado ? 0.28 : 1}
              className="cursor-pointer"
              style={{
                transition:
                  'stroke-dasharray 700ms cubic-bezier(0.16,1,0.3,1), opacity 200ms, stroke-width 200ms',
              }}
              onClick={() => seleccionar(segment.key)}
            />
          )
        })}
      </svg>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
        <span
          data-money
          className="font-display font-extrabold leading-none tracking-[-0.03em] text-ink"
          style={{ fontSize: cuerpoDelCentro(centerValue) }}
        >
          {centerValue}
        </span>
        <span className="mt-1 text-[10px] font-medium leading-tight text-ink-soft">
          {centerLabel}
        </span>
      </div>
    </div>
  )
}

interface Arco {
  segment: DonutSegment
  length: number
  offset: number
}

/**
 * Reparte la circunferencia entre los segmentos.
 *
 * El hueco y el arco mínimo se descuentan de los tramos grandes en lugar de
 * sumarse, para que la suma siga siendo exactamente la circunferencia y la dona
 * cierre sin solaparse.
 */
function repartirArcos(segments: readonly DonutSegment[], total: number): Arco[] {
  if (total <= 0 || segments.length === 0) return []

  // Con un solo segmento no hay nada que separar: el hueco dejaría una muesca
  // sin sentido en un anillo continuo.
  const hueco = segments.length > 1 ? GAP : 0
  const disponible = CIRCUMFERENCE - hueco * segments.length

  const crudos = segments.map((s) => (s.value / total) * disponible)
  const conMinimo = crudos.map((l) => Math.max(l, MIN_ARC))

  const exceso = conMinimo.reduce((a, b) => a + b, 0) - disponible
  const grandes = conMinimo.filter((l) => l > MIN_ARC)
  const sumaGrandes = grandes.reduce((a, b) => a + b, 0)

  const finales = conMinimo.map((l) =>
    l > MIN_ARC && sumaGrandes > 0 ? l - exceso * (l / sumaGrandes) : l,
  )

  let acumulado = 0
  return segments.map((segment, i) => {
    const length = finales[i]!
    const arco: Arco = { segment, length, offset: acumulado }
    acumulado += length + hueco
    return arco
  })
}

/**
 * Anillo de progreso: una sola métrica de 0 a 100.
 *
 * Se usa para el avance de una meta y para el uso de una tarjeta. Es la misma
 * forma que la dona a menor escala, así que ambas se leen con el mismo gesto
 * mental en vez de obligar a cambiar de lenguaje visual entre pantallas.
 */
export function ProgressRing({
  percent,
  size = 44,
  stroke = 5,
  color = 'var(--primary)',
  children,
  className,
}: {
  percent: number
  size?: number
  stroke?: number
  color?: string
  children?: React.ReactNode
  className?: string
}) {
  const [drawn, setDrawn] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const center = size / 2
  const radius = center - stroke / 2
  const circumference = 2 * Math.PI * radius
  const acotado = Math.min(100, Math.max(0, percent))
  const length = (acotado / 100) * circumference

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={Math.round(acotado)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg viewBox={`0 0 ${size} ${size}`} className="size-full -rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--surface-sunken)"
          strokeWidth={stroke}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={drawn ? `${length} ${circumference - length}` : `0 ${circumference}`}
          style={{ transition: 'stroke-dasharray 700ms cubic-bezier(0.16,1,0.3,1)' }}
        />
      </svg>
      {children ? (
        <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      ) : null}
    </div>
  )
}

/**
 * Color de un segmento por posición en la rampa de gráficas.
 *
 * Se usa con los segmentos ya ordenados de mayor a menor, para que los tramos
 * grandes reciban siempre los colores distintivos y los pequeños los neutros.
 * Nunca toma colores de la paleta de identidad: un mismo tono no puede
 * significar «de Ana» en una fila y «Restaurantes» en una dona.
 */
export function chartColor(index: number): string {
  return `var(--chart-${(index % 8) + 1})`
}
